/**
 * Engine `decision.action` shapes from holding-service
 * `portfolioOptimizerV2/audit/decisionLog.ts`. Insights parse this prose;
 * keep matchers here so advisor copy and AI context fail together if HS
 * changes the log format.
 */

export const CASH_RAISE_PREFIX_PATTERN = /^Raised\s/i;
export const CASH_DEPLOY_PREFIX_PATTERN = /^Deployed\s/i;
export const CASH_RAISE_CENTS_PATTERN = /^Raised (\d+)c\b/i;
export const CASH_DEPLOY_CENTS_PATTERN = /^Deployed (\d+)c\b/i;
export const CASH_RAISE_HOLDINGS_PATTERN = /^Raised (\d+)c from (\d+) holding/i;
export const CASH_DEPLOY_HOLDINGS_PATTERN =
  /^Deployed (\d+)c across (\d+) holding/i;
export const ZERO_CASH_MODE_PATTERN = /^(?:Raised|Deployed) 0c\b/i;
export const ZERO_DEPLOY_CASH_MODE_PATTERN = /^Deployed 0c\b/i;

export const TURNOVER_PCT_PATTERN = /turnover:\s*([\d.]+)%/i;
export const TURNOVER_BREAKDOWN_PATTERN =
  /turnover:\s*([\d.]+)%\s*\(buy\s*([\d.]+)%,\s*sell\s*([\d.]+)%,\s*(\d+)\s*holdings?\)/i;

export const INACTIVE_ZERO_LIQUIDATION_PATTERN =
  /fully liquidated: expectedCashCents >= marketValueCents \(MV=0\)/i;
export const LIQUIDATION_SUMMARY_PATTERN =
  /^(.+?) fully liquidated: (.+?) \(MV=(\d+)\)$/i;

export const FUNDING_SEGREGATION_APPLIED_PATTERN = /segregation applied/i;
export const FUNDING_SEGREGATION_NOT_APPLIED_PATTERN = /not applied/i;
export const STRUCTURAL_FUNDING_DRIFT_PATTERN = /structural funding drift/i;

/** Simplified CAD/USD prose used in FE tests (not emitted by HS). */
export const STRUCTURAL_FUNDING_DRIFT_SIMPLIFIED_PATTERN =
  /Structural funding drift:\s*CAD model\s+(\d+(?:\.\d+)?)%\s+vs fundable\s+(\d+(?:\.\d+)?)%;\s*USD model\s+(\d+(?:\.\d+)?)%\s+vs fundable\s+(\d+(?:\.\d+)?)%/i;

/** `decisionLog` fundingDriftDetected: `CAD UNDERFUNDED 15.0pp (model 60.0% vs fundable 45.0%)`. */
export const STRUCTURAL_FUNDING_DRIFT_HS_POOL_PATTERN =
  /\b([A-Z]{3})\s+(?:UNDERFUNDED|OVERFUNDED)\s+[\d.]+pp\s+\(model\s+([\d.]+)%\s+vs fundable\s+([\d.]+)%\)/gi;

export type StructuralFundingDriftShares = {
  cadModelPct: string;
  cadFundablePct: string;
  usdModelPct: string;
  usdFundablePct: string;
};

export const isCashRaiseAction = (action: string): boolean =>
  CASH_RAISE_PREFIX_PATTERN.test(action);

export const isCashDeployAction = (action: string): boolean =>
  CASH_DEPLOY_PREFIX_PATTERN.test(action);

export const parseCashRaiseCents = (action: string): number | null => {
  const match = action.match(CASH_RAISE_CENTS_PATTERN);
  return match ? Number(match[1]) : null;
};

export const parseCashDeployCents = (action: string): number | null => {
  const match = action.match(CASH_DEPLOY_CENTS_PATTERN);
  return match ? Number(match[1]) : null;
};

export const parseStructuralFundingDriftShares = (
  action: string,
): StructuralFundingDriftShares | null => {
  const simplified = action.match(STRUCTURAL_FUNDING_DRIFT_SIMPLIFIED_PATTERN);
  if (simplified) {
    const [, cadModelPct, cadFundablePct, usdModelPct, usdFundablePct] =
      simplified;
    return { cadModelPct, cadFundablePct, usdModelPct, usdFundablePct };
  }

  const pools = [
    ...action.matchAll(
      new RegExp(STRUCTURAL_FUNDING_DRIFT_HS_POOL_PATTERN.source, 'gi'),
    ),
  ];
  const byCurrency = new Map(
    pools.map((match) => [
      match[1].toUpperCase(),
      { modelPct: match[2], fundablePct: match[3] },
    ]),
  );
  const cad = byCurrency.get('CAD');
  const usd = byCurrency.get('USD');
  if (!cad || !usd) {
    return null;
  }

  return {
    cadModelPct: cad.modelPct,
    cadFundablePct: cad.fundablePct,
    usdModelPct: usd.modelPct,
    usdFundablePct: usd.fundablePct,
  };
};
